import { DrawProduct, DuoMeasurementValues, View } from "@novorender/api";
import { useEffect, useRef, useState } from "react";

import { isAxisPartName, isAxisPartRedundant } from "features/engine2D";

/**
 * Narrows an object-to-object measurement's draw product down to the axis lines worth drawing: the
 * three axis parts, minus any that just retrace the measured line itself. Shares the parts with the
 * product it came from, so reprojecting that product keeps these current too.
 */
export function getAxisDrawProduct(product: DrawProduct, result: DuoMeasurementValues): DrawProduct | undefined {
    const from = result.measureInfoA?.point;
    const to = result.measureInfoB?.point;
    const object = product.objects[0];
    if (!from || !to || !object) {
        return undefined;
    }

    const parts = object.parts.filter((part) => isAxisPartName(part.name) && !isAxisPartRedundant(part, from, to));

    return parts.length > 0 ? { ...product, objects: [{ ...object, parts }] } : undefined;
}

/**
 * The axis lines of the current object-to-object measurement, for consumers that have the result but
 * not the canvas' draw product - the labels, mainly, which need each axis' endpoints to sit on.
 */
export function useObjectMeasureAxisDrawProduct(view: View | undefined, result: DuoMeasurementValues | undefined) {
    const [drawProduct, setDrawProduct] = useState<DrawProduct | undefined>(undefined);
    const cacheRef = useRef<{ result: DuoMeasurementValues; product: DrawProduct | undefined } | undefined>(undefined);

    useEffect(() => {
        const abortController = new AbortController();
        getDrawProduct();

        async function getDrawProduct() {
            const measureView = view?.measure;
            if (!measureView || !result) {
                setDrawProduct(undefined);
                return;
            }

            if (cacheRef.current?.result === result) {
                setDrawProduct(cacheRef.current.product);
                return;
            }

            const product = await measureView.draw.getDrawEntity(result);
            if (abortController.signal.aborted) {
                return;
            }

            const axisProduct = product ? getAxisDrawProduct(product, result) : undefined;
            cacheRef.current = { result, product: axisProduct };
            setDrawProduct(axisProduct);
        }

        return () => {
            abortController.abort();
        };
    }, [view, result]);

    return drawProduct;
}
